const destinations = [
  {
    name: "Caribbean Islands",
    context: "Bahamas, Jamaica, St. Thomas, and Eastern Caribbean cruises",
    urgency: "Family cruises booked 30–60 days out during promotional windows",
    icon: "ri-map-pin-2-line",
    flag: "BS",
  },
  {
    name: "Canada",
    context: "Toronto spring training road trips, Montreal culture, Vancouver, and fishing in Ontario",
    urgency: "Spring training fans often extend to Toronto or Montreal in March",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
  {
    name: "Mexico",
    context: "Cancun, Riviera Maya, and Cozumel spring break family trips",
    urgency: "Spring break bookings happen January–February for March departure",
    icon: "ri-map-pin-2-line",
    flag: "MX",
  },
  {
    name: "Costa Rica & Central America",
    context: "Eco-tours, zip-lining, rainforest lodges, and family adventure travel",
    urgency: "Eco-tourism packages booked 6–10 weeks before summer departures",
    icon: "ri-map-pin-2-line",
    flag: "CR",
  },
  {
    name: "United Kingdom",
    context: "London sightseeing, Scottish heritage trips, and European connections",
    urgency: "Heritage and genealogy trips often planned 2–3 months out",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "Europe",
    context: "Italy, France, and Spain for retirees and multi-generational family trips",
    urgency: "Summer European tours booked during winter promotional sales",
    icon: "ri-map-pin-2-line",
    flag: "EU",
  },
];

const commonSituations = [
  {
    stat: "68%",
    label: "of Fort Myers expedited requests are family-related (multiple passports at once)",
  },
  {
    stat: "4–8 Weeks",
    label: "average notice Fort Myers travelers have before a planned international trip",
  },
  {
    stat: "Feb–Mar",
    label: "peak season when spring training visitors discover passport issues",
  },
  {
    stat: "36 Hours",
    label: "shortest cruise departure we have accommodated for a Fort Myers family",
  },
];

export default function FortMyersDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Fort Myers Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Lee County clients. Understanding local travel behavior is part of why our document review catches issues before they derail your family vacation or spring training road trip.
          </p>
        </div>

        {/* Stat block row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Destinations grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal linking block */}
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Southwest and Southeast Florida with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/naples" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Naples <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/tampa" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tampa <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/orlando" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Orlando <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}